import React from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { generatePath, useHistory } from 'react-router-dom';

import { clearSelectedThreadMessage } from 'flow/actions/chat-actions';
import { AvatarSize } from 'flow/components/ui-components/ChatAvatar';
import ExtensionContactAvatar from 'flow/components/ui-components/ExtensionContactAvatar';
import { useRowTabIndex } from 'flow/components/ui-components/row-tab-stop';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { extractName } from 'flow/utils';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { ChatMember, From } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';

interface Props {
    from: TypedMap<From | ChatMember>;
    avatarOnly?: boolean;
    size?: AvatarSize;
    chatSessionKey: ChatSessionKey;
}

const FromAvatar = ({ from, avatarOnly, size, chatSessionKey }: Props) => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const { closeModal } = useModal();
    const dispatch = useAppDispatch();
    const rowTabIndex = useRowTabIndex();

    const extensionKey = from?.get('extensionKey');
    const { extension, status: selectedStatus } = useExtension(extensionKey);
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const status = avatarOnly ? undefined : selectedStatus;

    // The address is read at the click. Reading it with useLocation or
    // useParams would render every avatar again on each change of the address.
    const onClick = () => {
        closeModal();
        dispatch(clearSelectedThreadMessage());
        history.push(
            generatePath(routes['messages.id.settings.contact.key'], {
                extensionKey: extensionKey,
                id: chatSessionKey,
            }),
            { from: history.location.pathname },
        );
    };

    return (
        <button
            aria-label={extractName(
                from?.get('contact'),
                i18next.t('Contact.Unknown'),
            )}
            onClick={onClick}
            tabIndex={rowTabIndex}
            className="flex"
            disabled={
                !extension?.get('key') || extensionKey === ownExtensionKey
            }
        >
            <ExtensionContactAvatar
                contact={from?.get('contact')}
                status={status}
                size={size}
            />
        </button>
    );
};

export default FromAvatar;
